import React, { useEffect, useRef, useState } from 'react';
import api from '../api';

const validUrl = url => /^https?:\/\//i.test(url || '');

function InlineAnswer({ text, sources }) {
  return text.split(/(\*\*[^*]+\*\*|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\)|\[\d+\])/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (link) return <a key={i} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]} ↗</a>;
    const ref = part.match(/^\[(\d+)\]$/);
    const source = ref && sources.find(s => String(s.ref) === ref[1]);
    if (source && validUrl(source.url)) {
      return <a key={i} href={source.url} target="_blank" rel="noopener noreferrer">{part}</a>;
    }
    return part;
  });
}

function Answer({ text, sources = [] }) {
  return (
    <div style={{ lineHeight: 1.6, marginTop: 8, overflowWrap: 'anywhere' }}>
      {(text || '').split(/\r?\n/).map((line, i) => {
        if (!line.trim()) return <div key={i} style={{ height: 8 }} />;
        const heading = line.match(/^\s*#{1,4}\s+(.*)$/);
        const bullet = line.match(/^\s*[-*]\s+(.*)$/);
        const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
        const body = heading?.[1] || bullet?.[1] || numbered?.[2] || line;
        return (
          <div key={i} style={{ paddingLeft: bullet || numbered ? 16 : 0,
                                fontWeight: heading ? 600 : undefined, marginBottom: 3 }}>
            {bullet ? '• ' : numbered ? `${numbered[1]}. ` : ''}
            <InlineAnswer text={body} sources={sources} />
          </div>
        );
      })}
    </div>
  );
}


export default function CompanyQuestions({ ticker }) {
  const historyRef = useRef(null);
  const pendingRef = useRef(false);
  const [question, setQuestion] = useState('');
  const [items, setItems] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setItems([]);
    setHistoryOpen(false);
    pendingRef.current = false;
    setQuestion('');
    setError('');
    const refresh = () => api.get('/company/questions', { params: { ticker } })
      .then(({ data }) => { if (active) {
        setItems(data.items);
        pendingRef.current = data.items.some(item => ['queued', 'running'].includes(item.status));
      }}).catch(() => {});
    refresh();
    const timer = setInterval(() => { if (pendingRef.current) refresh(); }, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [ticker]);

  const send = e => {
    e.preventDefault();
    if (!question.trim() || sending) return;
    setHistoryOpen(true);
    setSending(true);
    pendingRef.current = true;
    setError('');
    api.post('/company/questions', { ticker, question: question.trim() })
      .then(() => {
        setQuestion('');
        if (historyRef.current) historyRef.current.scrollTop = 0;
        return api.get('/company/questions', { params: { ticker } })
          .then(({ data }) => {
            setItems(data.items);
            pendingRef.current = data.items.some(item => ['queued', 'running'].includes(item.status));
          });
      })
      .catch(e => {
        pendingRef.current = false;
        setError(e?.response?.data?.error || 'Could not submit question');
      })
      .finally(() => setSending(false));
  };

  return (
    <div className="co-block" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h4 style={{ margin: 0 }}>Ask Woody</h4>
        <button type="button" className="co-aibtn" aria-expanded={historyOpen}
                aria-controls="co-woody-history" onClick={() => setHistoryOpen(open => !open)}>
          {historyOpen ? 'Hide conversation −' : `Conversation (${items.length}) +`}
        </button>
      </div>
      <form onSubmit={send} style={{ marginTop: 12 }}>
        <textarea rows={2} value={question} maxLength={2000}
          onChange={e => setQuestion(e.target.value)}
          placeholder="Ask about the company's announcements, finances or risks"
          style={{ boxSizing: 'border-box', width: '100%', padding: 10, resize: 'vertical',
                   border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                   background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Announcements first; outside sources if needed. AI answers may be incorrect.
          </span>
          <button type="submit" className="co-aibtn" disabled={!question.trim() || sending}>
            {sending ? 'Sending…' : 'Ask Woody'}
          </button>
        </div>
      </form>
      {error && <p style={{ color: 'var(--error)', fontSize: 12 }}>{error}</p>}
      {historyOpen && <div id="co-woody-history" ref={historyRef}
           style={{ maxHeight: 440, overflowY: 'auto', marginTop: 14 }}
           aria-label="Woody Bot question history">
        {items.map(item => (
          <div key={item.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, paddingBottom: 12, fontSize: 13 }}>
            <b>{item.question}</b>
            {item.status === 'queued' || item.status === 'running'
              ? <p className="co-aiwait">{item.status === 'queued' ? 'Queued…' : 'Woody Bot is researching…'}</p>
              : item.status === 'error'
                ? <p style={{ color: 'var(--error)' }}>{item.error || 'Could not answer'}</p>
                : <>
                    <Answer text={item.answer} sources={item.sources} />
                    {!!item.sources?.length && (
                      <div style={{ fontSize: 11, marginTop: 10 }}>
                        <b>Sources</b>
                        {item.sources.filter(s => validUrl(s.url)).map((s, i) => (
                          <div key={i}>
                            {s.ref ? `[${s.ref}] ` : ''}
                            <a href={s.url} target="_blank" rel="noopener noreferrer">{s.label || s.url} ↗</a>
                          </div>
                        ))}
                      </div>
                    )}
                  </>}
          </div>
        ))}
      </div>}
    </div>
  );
}
